import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, LogOut } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";

export default function ForcePasswordChangePage() {
  const { user, logout, refresh } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Welcome to Ledger.");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-6 py-12 bg-white" data-testid="force-password-change-page">
      <div className="max-w-md w-full">
        <div className="mb-8 text-neutral-900">
          <CoreLogo variant="inline" size={42} />
        </div>
        <div className="border-l-2 border-amber-500 pl-4 mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-1 flex items-center gap-1.5">
            <ShieldAlert className="h-3 w-3" strokeWidth={1.5} /> Action Required
          </div>
          <div className="font-serif text-2xl text-neutral-900">Set a new password</div>
        </div>
        <p className="text-sm text-neutral-600 mb-8">
          Your brokerage admin reset your password. Please choose a new one before continuing. You&apos;re signed in as{" "}
          <span className="font-mono text-neutral-900">{user?.email}</span>.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="force-change-form">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Temporary Password (from admin)</Label>
            <Input
              type="password"
              required
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              autoComplete="current-password"
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
              data-testid="force-current-password-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">New Password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              autoComplete="new-password"
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
              data-testid="force-new-password-input"
            />
            <p className="text-xs text-neutral-500 mt-1">At least 6 characters.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Confirm New Password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              autoComplete="new-password"
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
              data-testid="force-confirm-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-11 font-sans tracking-wide"
            data-testid="force-submit-button"
          >
            <KeyRound className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {saving ? "Updating…" : "Set new password & continue"}
          </Button>
        </form>

        <button
          onClick={async () => { await logout(); window.location.href = "/login"; }}
          className="mt-8 pt-6 border-t border-neutral-200 inline-flex items-center gap-2 text-xs font-sans uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900"
          data-testid="force-signout-link"
        >
          <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
          Sign out instead
        </button>
      </div>
    </div>
  );
}
